import React, { useCallback, useEffect, useState, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as revenueService from '../services/revenueService';
import PendingPaymentsWidget from '../components/PendingPaymentsWidget';
import PatientInvoiceBillingModal from '../components/PatientInvoiceBillingModal';
import ConsultationPaymentModal from '../components/ConsultationPaymentModal';
import web_logo from '../assets/images/web_logo.png';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../services/apiHelpers';

/* ═══════════════════════════════════════════════════════════
   CLINIQUEUE — REVENUE OPERATIONS DASHBOARD
═══════════════════════════════════════════════════════════ */

const formatINR = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(v || 0);

// ─── Inline SVG Clean Vector Icon Pack ──────────────────────
const SVGAsset = {
  location: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
  calendar: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,
  users: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  trend: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 17"/><polyline points="17 6 23 6 23 12"/></svg>`,
  check: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
  clock: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
  dollar: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>`,
  search: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  refresh: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>`,
  download: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`
};

// ─── Animated Counter Engine ────────────────────────────────
function AnimCounter({ target, isINR = true }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    setVal(0);
    if (!target) return;
    const duration = 750;
    const step = target / (duration / 16);
    let current = 0;
    const timer = setInterval(() => {
      current = Math.min(current + step, target);
      setVal(Math.floor(current));
      if (current >= target) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [target]);
  if (isINR) return <>{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(val)}</>;
  return <>{val.toLocaleString('en-IN')}</>;
}

// ─── Inline Sparkline Component ─────────────────────────────
function Sparkline({ data = [], color = '#10b981' }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.revenue || 0);
  const max = Math.max(...vals) || 1;
  const min = Math.min(...vals);
  const range = max - min || 1;
  const w = 110, h = 34;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} style={{ overflow: 'visible', display: 'block' }}>
      <defs>
        <linearGradient id={`sg${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#sg${color.replace('#','')})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ─── Operational Bar Chart Trend ────────────────────────────
function BarChart({ data = [] }) {
  const [hovered, setHovered] = useState(null);
  if (!data || data.length === 0) return (
    <div style={{ height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 13, fontWeight: 500 }}>No chart data logs available</div>
  );
  const maxVal = Math.max(...data.map(d => d.revenue || 0)) || 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 150, padding: '12px 4px 0', boxSizing: 'border-box' }}>
      {data.map((d, i) => {
        const pct = ((d.revenue || 0) / maxVal) * 100;
        const isHov = hovered === i;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            <div style={{ fontSize: 10, fontWeight: 700, color: isHov ? '#1a56e8' : 'transparent', fontFamily: "'JetBrains Mono', monospace", transition: 'color 0.15s', whiteSpace: 'nowrap' }}>
              {formatINR(d.revenue)}
            </div>
            <div style={{
              width: '100%', height: `${Math.max(pct, 6)}%`,
              background: isHov
                ? 'linear-gradient(180deg, #1a56e8 0%, #1248d0 100%)'
                : 'linear-gradient(180deg, rgba(26, 86, 232, 0.25) 0%, rgba(26, 86, 232, 0.05) 100%)',
              border: isHov ? '1px solid #1a56e8' : '1px solid rgba(26, 86, 232, 0.12)',
              borderRadius: '4px 4px 0 0',
              transition: 'all 0.2s ease',
            }} />
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{d.date}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Allocation Framework Donut Matrix ──────────────────────
function PaymentDonutChart({ cashPct = 0, upiPct = 0, cashValue = 0, upiValue = 0 }) {
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  
  const totalInputPct = cashPct + upiPct;
  const normalizedCashPct = totalInputPct > 0 ? (cashPct / totalInputPct) * 100 : 0;
  const normalizedUpiPct = totalInputPct > 0 ? (upiPct / totalInputPct) * 100 : 0;

  const cashArc = (normalizedCashPct / 100) * circumference;
  const upiArc = (normalizedUpiPct / 100) * circumference;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center', justifyContent: 'center', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', position: 'relative', alignItems: 'center' }}>
        <svg width="160" height="160" viewBox="0 0 180 180" style={{ flexShrink: 0, display: 'block' }}>
          <circle cx="90" cy="90" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="16" />
          {totalInputPct > 0 && (
            <>
              <circle
                cx="90" cy="90" r={radius} fill="none" stroke="#10b981" strokeWidth="16"
                strokeDasharray={`${cashArc} ${circumference}`}
                strokeDashoffset={0}
                transform="rotate(-90 90 90)"
              />
              <circle
                cx="90" cy="90" r={radius} fill="none" stroke="#0ea5e9" strokeWidth="16"
                strokeDasharray={`${upiArc} ${circumference}`}
                strokeDashoffset={-cashArc}
                transform="rotate(-90 90 90)"
              />
            </>
          )}
        </svg>
        <div style={{ position: 'absolute', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', pointerEvents: 'none' }}>
          <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Allocation</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{cashPct + upiPct}%</div>
        </div>
      </div>
      <div style={{ display: 'grid', gap: 10, width: '100%', maxWidth: 260, margin: '0 auto' }}>
        {[
          { label: 'Cash Payment', amount: cashValue, pct: cashPct, color: '#10b981' },
          { label: 'UPI Payment', amount: upiValue, pct: upiPct, color: '#0ea5e9' },
        ].map((item, idx) => (
          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 10, height: 10, borderRadius: '3px', background: item.color, display: 'inline-block' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{item.label}</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(item.amount)}</div>
              <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 500 }}>{item.pct}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component Cockpit ──────────────────────────────────
const RevenuePage = () => {
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('today');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [viewMode, setViewMode] = useState('clinic');
  const [searchQuery, setSearchQuery] = useState('');
  const searchQueryRef = useRef('');
  const pendingSearchTimeoutRef = useRef(null);
  const isSearchInitialMount = useRef(true);

  const [overview, setOverview] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [doctorBreakdown, setDoctorBreakdown] = useState(null);
  const [pendingPayments, setPendingPayments] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [subscriptionClinicBreakdown, setSubscriptionClinicBreakdown] = useState(null);
  const [subscriptionPlanBreakdown, setSubscriptionPlanBreakdown] = useState(null);
  const [revenueType, setRevenueType] = useState('clinic'); // 'clinic' or 'subscription'

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentAppointment, setSelectedPaymentAppointment] = useState(null);
  const [showRevenuePaymentModal, setShowRevenuePaymentModal] = useState(false);
  const [patientReceiptEnabled, setPatientReceiptEnabled] = useState(false);
  const [doctorReceiptSettings, setDoctorReceiptSettings] = useState({});
  const [mounted, setMounted] = useState(false);
  const { auth } = useAuth();
  const [clinicSettings, setClinicSettings] = useState({
    name: auth?.clinic?.name || (typeof window !== 'undefined' && window.__CLINIC_NAME__) || 'Clinic Name',
    address: (typeof window !== 'undefined' && window.__CLINIC_ADDRESS__) || 'Clinic Address',
    code: auth?.clinic?.code || (typeof window !== 'undefined' && window.__CLINIC_CODE__) || '',
    id: auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__) || ''
  });

  useEffect(() => {
    const clinicId = auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__);
    if (!clinicId) return;
    fetch(buildApiUrl(`subscription/dashboard/${clinicId}`), { headers: { 'X-Clinic-Id': clinicId } })
      .then(response => response.ok ? response.json() : null)
      .then(data => setPatientReceiptEnabled(data?.patientReceiptEnabled === true))
      .catch(() => setPatientReceiptEnabled(false));
  }, [auth?.clinic?.id]);

  useEffect(() => {
    const clinicId = auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__);
    if (!clinicId) return;
    fetch(buildApiUrl(`doctors/by-clinic-id/${clinicId}`), { headers: { 'X-Clinic-Id': clinicId } })
      .then(response => response.ok ? response.json() : [])
      .then(doctors => setDoctorReceiptSettings(Object.fromEntries((Array.isArray(doctors) ? doctors : []).map(doctor => [doctor.id, doctor.enable_patient_receipts === true]))))
      .catch(() => setDoctorReceiptSettings({}));
  }, [auth?.clinic?.id]);

  const loadDoctors = async () => {
    try {
      const clinicId = auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__);
      if (!clinicId) { setDoctors([{ id: null, doctorName: 'All Doctors' }]); return; }
      const res = await fetch(buildApiUrl(`doctors/by-clinic-id/${clinicId}`));
      const data = await res.json();
      const normalized = Array.isArray(data)
        ? data.map(d => ({ ...d, doctorName: d.doctorName || d.doctor_name || '' }))
        : [];
      setDoctors([{ id: null, doctorName: 'All Doctors' }, ...normalized]);
    } catch {
      setDoctors([{ id: null, doctorName: 'All Doctors' }]);
    }
  };

  const getDateRange = useCallback(() => {
    const now = new Date();
    switch (filterType) {
      case 'today':
        return { startDate: now.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
      case 'month':
        return { startDate: `${selectedMonth}-01`, endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0] };
      case 'year':
        return { startDate: `${selectedYear}-01-01`, endDate: `${selectedYear}-12-31` };
      case 'customRange':
        return { startDate, endDate };
      default:
        return { startDate, endDate };
    }
  }, [filterType, selectedMonth, selectedYear, startDate, endDate]);

  const loadPendingPayments = useCallback(async (query, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const dateRange = getDateRange();
      const pendingRes = await revenueService.getPendingPayments(100, 0, dateRange.startDate, dateRange.endDate, query);
      if (pendingRes?.pending && Array.isArray(pendingRes.pending)) {
        pendingRes.pending.sort((a, b) => new Date(b.appointmentDate || 0) - new Date(a.appointmentDate || 0));
      }
      setPendingPayments(pendingRes);
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    if (isSearchInitialMount.current) {
      isSearchInitialMount.current = false;
      return;
    }

    if (pendingSearchTimeoutRef.current) {
      clearTimeout(pendingSearchTimeoutRef.current);
    }

    pendingSearchTimeoutRef.current = setTimeout(() => {
      loadPendingPayments(searchQueryRef.current, true);
    }, 500);

    return () => {
      if (pendingSearchTimeoutRef.current) {
        clearTimeout(pendingSearchTimeoutRef.current);
      }
    };
  }, [searchQuery, loadPendingPayments]);

  const loadData = useCallback(async (opts = {}) => {
    const { silent = false } = opts;

    if (!silent) setLoading(true);
    try {
      const dateRange = getDateRange();

      if (revenueType === 'subscription') {
        // Load subscription revenue data
        const overviewData = await revenueService.getSubscriptionRevenueOverview(dateRange.startDate, dateRange.endDate);
        setOverview(overviewData);

        if (filterType === 'today') {
          const trendRes = await revenueService.getSubscriptionRevenueTrend(7);
          setTrendData(trendRes);
        } else {
          setTrendData(null);
        }

        if (filterType === 'month') {
          const monthlyRes = await revenueService.getSubscriptionMonthlySummary(selectedMonth);
          setMonthlySummary(monthlyRes);
        } else {
          setMonthlySummary(null);
        }

        // Load clinic and plan breakdown
        const clinicRes = await revenueService.getSubscriptionRevenueByClinic(dateRange.startDate, dateRange.endDate);
        setSubscriptionClinicBreakdown(clinicRes);

        const planRes = await revenueService.getSubscriptionRevenueByPlan(dateRange.startDate, dateRange.endDate);
        setSubscriptionPlanBreakdown(planRes);

        // Load pending invoices
        const pendingRes = await revenueService.getPendingSubscriptionInvoices(100, 0, dateRange.startDate, dateRange.endDate, searchQueryRef.current);
        if (pendingRes?.pending && Array.isArray(pendingRes.pending)) {
          pendingRes.pending.sort((a, b) => new Date(b.generatedAt || 0) - new Date(a.generatedAt || 0));
        }
        setPendingPayments(pendingRes);
      } else {
        // Load clinic revenue data (existing logic)
        const overviewData = await revenueService.getRevenueOverview(filterType, selectedDoctor?.id, dateRange.startDate, dateRange.endDate);
        setOverview(overviewData);

        if (filterType === 'today') {
          const trendRes = await revenueService.getRevenueTrend(7);
          setTrendData(trendRes);
        } else {
          setTrendData(null);
        }

        if (filterType === 'month') {
          const monthlyRes = await revenueService.getMonthlySummary(selectedMonth);
          setMonthlySummary(monthlyRes);
        } else {
          setMonthlySummary(null);
        }

        if (viewMode === 'clinic' && !selectedDoctor) {
          const breakdownRes = await revenueService.getDoctorBreakdown(filterType, dateRange.startDate, dateRange.endDate);
          setDoctorBreakdown(breakdownRes);
        }

        const pendingRes = await revenueService.getPendingPayments(100, 0, dateRange.startDate, dateRange.endDate, searchQueryRef.current);
        if (pendingRes?.pending && Array.isArray(pendingRes.pending)) {
          pendingRes.pending.sort((a, b) => new Date(b.appointmentDate || 0) - new Date(a.appointmentDate || 0));
        }
        setPendingPayments(pendingRes);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filterType, selectedDoctor, selectedMonth, viewMode, getDateRange, revenueType]);

  useEffect(() => { loadDoctors(); setTimeout(() => setMounted(true), 60); }, []);

  useEffect(() => {
    const clinicId = auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__);
    const fallbackName = auth?.clinic?.name || (typeof window !== 'undefined' && window.__CLINIC_NAME__) || 'Clinic Name';
    const fallbackAddress = (typeof window !== 'undefined' && window.__CLINIC_ADDRESS__) || 'Clinic Address';
    if (!clinicId) return;

    const fetchClinicSettings = async () => {
      try {
        const res = await fetch(buildApiUrl(`settings/clinic-profile/${clinicId}`));
        if (!res.ok) throw new Error('Failed to fetch clinic settings profile');
        const data = await res.json();

        const clinicAddress = [
          data.address_line || data.clinicAddress || data.clinicAddressLine || '',
          `${data.city || ''}${data.city && data.state_name ? ', ' : ''}${data.state_name || ''}`.trim(),
          data.pincode || data.clinicPincode || ''
        ].filter(Boolean).join(', ');

        setClinicSettings({
          name: data.clinic_name || data.clinicName || fallbackName,
          address: clinicAddress || fallbackAddress,
          code: auth?.clinic?.code || (typeof window !== 'undefined' && window.__CLINIC_CODE__) || '',
          id: clinicId,
        });
      } catch (err) {
        console.error('Clinic Configuration Mapping Failure:', err);
      }
    };

    fetchClinicSettings();
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('paymentCollected', handler);
    return () => window.removeEventListener('paymentCollected', handler);
  }, [loadData]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadData({ silent: true });
    }, 10000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleDoctorChange = (doctor) => {
    setSelectedDoctor(doctor);
    setViewMode(doctor?.id ? 'doctor' : 'clinic');
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    searchQueryRef.current = query;
  };

  // Static strings moved down cleanly out of top-level scope contexts
  const exportPdf = async () => {
    try {
      const clinicName = clinicSettings.name || auth?.clinic?.name || (typeof window !== 'undefined' && window.__CLINIC_NAME__) || 'Clinic Name';
      const clinicCode = clinicSettings.code || auth?.clinic?.code || (typeof window !== 'undefined' && window.__CLINIC_CODE__) || '';
      const clinicAddr = clinicSettings.address || (typeof window !== 'undefined' && window.__CLINIC_ADDRESS__) || 'Clinic Address';
      const web_logoUrl = web_logo;
      const fileName = `revenue-ledger-${new Date().toISOString().slice(0,10)}.pdf`;

      const reportTarget = selectedDoctor?.doctorName ? `Provider Focus: ${selectedDoctor.doctorName}` : 'All Providers Overview';
      const periodLabel = filterType === 'today'
        ? `Today (${new Date().toLocaleDateString('en-IN')})`
        : filterType === 'month'
          ? `Month: ${new Date(`${selectedMonth}-01`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}`
          : filterType === 'year' ? `Year: ${selectedYear}` : `Custom Cycle: ${startDate} to ${endDate}`;

      const cashValue = activeData?.cashRevenue || 0;
      const upiValue = activeData?.upiRevenue || 0;
      const totalPayment = cashValue + upiValue || 1;
      const cashPct = Math.round((cashValue / totalPayment) * 100);
      const upiPct = Math.round((upiValue / totalPayment) * 100);

      const pageMarginMm = 12;
      const pageWidthPx = 1000;
      const donutRadius = 65;
      const donutCircumference = 2 * Math.PI * donutRadius;
      
      const cashArc = (cashPct / 100) * donutCircumference;
      const upiArc = (upiPct / 100) * donutCircumference;

      const headerNode = document.createElement('div');
      const bodyNode = document.createElement('div');
      [headerNode, bodyNode].forEach(el => {
        el.style.width = `${pageWidthPx}px`;
        el.style.background = '#ffffff';
        el.style.color = '#111827';
        el.style.boxSizing = 'border-box';
        el.style.fontFamily = "system-ui, -apple-system, sans-serif";
      });

      const styleTag = document.createElement('style');
      styleTag.textContent = `
        * { margin: 0; padding: 0; box-sizing: border-box; }
        .pdf-heading { text-align: center; margin-bottom: 16px; }
        .pdf-heading-title { font-size: 38px; font-weight: 700; color: #1e3a8a; letter-spacing: -0.5px; }
        .pdf-header { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 20px; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 12px; }
        .pdf-brand { display: flex; align-items: center; gap: 16px; }
        .pdf-web_logo { width: 200px; height: auto; object-fit: contain; }
        .pdf-title { font-size: 24px; font-weight: 700; color: #111827; }
        .pdf-code { font-size: 12px; font-weight: 600; color: #1e3a8a; letter-spacing: 0.5px; margin-top: 4px; }
        .pdf-subtitle { font-size: 13px; color: #6b7280; margin-top: 6px; display: flex; align-items: center; gap: 6px; }
        .pdf-header-info { text-align: right; }
        .pdf-chip { display: inline-block; padding: 6px 14px; border-radius: 8px; background: #dbeafe; color: #1e40af; font-size: 12px; font-weight: 600; border: 1px solid #bfdbfe; }
        .pdf-section { margin-top: 24px; }
        .pdf-section-title { font-size: 16px; font-weight: 700; color: #111827; margin-bottom: 14px; display: flex; align-items: center; gap: 6px; text-transform: uppercase; letter-spacing: 0.5px; }
        .pdf-summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
        .pdf-metric-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; position: relative; }
        .pdf-metric-card::before { content:''; position:absolute; left:0; top:0; bottom:0; width:3px; background:#1e3a8a; border-radius:12px 0 0 12px; }
        .pdf-metric-label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; }
        .pdf-metric-value { font-size: 22px; font-weight: 700; color: #111827; margin-top: 4px; }
        .pdf-payment-section { border: 1px solid #e5e7eb; border-radius: 12px; padding: 20px; background: #f9fafb; display: flex; align-items: center; gap: 32px; justify-content: center; }
        .pdf-payment-legend { display: grid; gap: 12px; min-width: 220px; }
        .pdf-legend-item { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; background: #ffffff; border-radius: 8px; border: 1px solid #e5e7eb; }
        .pdf-legend-label { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; }
        .pdf-color-dot { width: 8px; height: 8px; border-radius: 2px; }
        .pdf-legend-amount { font-size: 13px; font-weight: 700; }
        .pdf-legend-pct { font-size: 11px; color: #9ca3af; }
        .pdf-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        .pdf-table th { padding: 10px 14px; background: #f9fafb; font-size: 11px; font-weight: 700; color: #374151; text-align: left; text-transform: uppercase; border-bottom: 1px solid #e5e7eb; }
        .pdf-table td { padding: 12px 14px; border-bottom: 1px solid #f3f4f6; font-size: 13px; color: #374151; }
      `;
      headerNode.appendChild(styleTag.cloneNode(true));
      bodyNode.appendChild(styleTag.cloneNode(true));

      headerNode.innerHTML += `
        <div class="pdf-heading"><h1 class="pdf-heading-title">REVENUE OPERATIONS REPORT</h1></div>
        <div class="pdf-header">
          <div class="pdf-brand">
            <img src="${web_logoUrl}" class="pdf-web_logo" alt="web_logo" />
            <div>
              <h1 class="pdf-title">${clinicName}</h1>
              ${clinicCode ? `<div class="pdf-code">${clinicCode}</div>` : ''}
              <div class="pdf-subtitle"><span>${SVGAsset.location}</span>${clinicAddr}</div>
            </div>
          </div>
          <div class="pdf-header-info">
            <div class="pdf-chip">${reportTarget}</div>
            <div class="pdf-subtitle" style="justify-content: flex-end; margin-top: 10px;"><span>${SVGAsset.calendar}</span>${periodLabel}</div>
            <div class="pdf-subtitle" style="justify-content: flex-end; margin-top: 4px;">Generated: ${new Date().toLocaleString('en-IN')}</div>
          </div>
        </div>
      `;

      bodyNode.innerHTML += `
        <div class="pdf-section">
          <h2 class="pdf-section-title"><span>${SVGAsset.trend}</span>Key Metrics</h2>
          <div class="pdf-summary-grid">
            <div class="pdf-metric-card"><p class="pdf-metric-label">Gross Revenue</p><p class="pdf-metric-value">${formatINR(activeData?.totalRevenue || 0)}</p></div>
            <div class="pdf-metric-card"><p class="pdf-metric-label">Realized Amount</p><p class="pdf-metric-value">${formatINR(activeData?.collectedRevenue || 0)}</p></div>
            <div class="pdf-metric-card"><p class="pdf-metric-label">Pending Balance</p><p class="pdf-metric-value">${formatINR(activeData?.pendingRevenue || 0)}</p></div>
            <div class="pdf-metric-card"><p class="pdf-metric-label">Patient Volume</p><p class="pdf-metric-value">${activeData?.patientCount || 0}</p></div>
          </div>
        </div>
        <div class="pdf-section">
          <h2 class="pdf-section-title"><span>${SVGAsset.clock}</span>Payment Allocation</h2>
          <div class="pdf-payment-section">
            <svg width="160" height="160" viewBox="0 0 180 180">
              <circle cx="90" cy="90" r="${donutRadius}" fill="none" stroke="#e5e7eb" stroke-width="18" />
              ${totalPayment > 1 ? `
                <circle cx="90" cy="90" r="${donutRadius}" fill="none" stroke="#047857" stroke-width="18" stroke-dasharray="${cashArc} ${donutCircumference}" transform="rotate(-90 90 90)" />
                <circle cx="90" cy="90" r="${donutRadius}" fill="none" stroke="#0891b2" stroke-width="18" stroke-dasharray="${upiArc} ${donutCircumference}" stroke-dashoffset="-${cashArc}" transform="rotate(-90 90 90)" />
              ` : ''}
              <text x="90" y="96" text-anchor="middle" font-size="24" fill="#111827" font-weight="700">${cashPct + upiPct}%</text>
            </svg>
            <div class="pdf-payment-legend">
              <div class="pdf-legend-item"><div class="pdf-legend-label"><span class="pdf-color-dot" style="background:#047857;"></span>Cash Allocation</div><div style="text-align:right;"><div class="pdf-legend-amount">${formatINR(cashValue)}</div><div class="pdf-legend-pct">${cashPct}%</div></div></div>
              <div class="pdf-legend-item"><div class="pdf-legend-label"><span class="pdf-color-dot" style="background:#0891b2;"></span>UPI Allocation</div><div style="text-align:right;"><div class="pdf-legend-amount">${formatINR(upiValue)}</div><div class="pdf-legend-pct">${upiPct}%</div></div></div>
            </div>
          </div>
        </div>
      `;

      if (breakdownSource && breakdownSource.length > 0) {
        const rowsHtml = breakdownSource.map(d => {
          const collected = d.collectedAmount ?? d.collected ?? d.revenue ?? 0;
          return `<tr><td>${d.doctorName || '—'}</td><td style="text-align:right; font-weight:700;">${formatINR(collected)}</td></tr>`;
        }).join('');

        bodyNode.innerHTML += `
          <div class="pdf-section">
            <h2 class="pdf-section-title"><span>${SVGAsset.users}</span>Provider Revenue</h2>
            <table class="pdf-table">
              <thead><tr><th>Doctor</th><th style="text-align:right;">Settled Revenue</th></tr></thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </div>
        `;
      }

      document.body.appendChild(headerNode);
      document.body.appendChild(bodyNode);

      const [headerCanvas, bodyCanvas] = await Promise.all([
        html2canvas(headerNode, { scale: 2, useCORS: true }),
        html2canvas(bodyNode, { scale: 2, useCORS: true }),
      ]);

      document.body.removeChild(headerNode);
      document.body.removeChild(bodyNode);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const contentWidthMm = pdfWidth - pageMarginMm * 2;
      const pxPerMm = bodyCanvas.width / contentWidthMm;
      const headerHeightMm = headerCanvas.height / pxPerMm;
      const footerHeightMm = 16;
      const contentHeightMm = pdfHeight - pageMarginMm - footerHeightMm - headerHeightMm - 4;
      const contentHeightPx = Math.floor(contentHeightMm * pxPerMm);
      const totalPages = Math.max(1, Math.ceil(bodyCanvas.height / contentHeightPx));

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const sliceHeightPx = Math.min(contentHeightPx, bodyCanvas.height - pageIndex * contentHeightPx);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = bodyCanvas.width;
        sliceCanvas.height = sliceHeightPx;
        sliceCanvas.getContext('2d').drawImage(bodyCanvas, 0, pageIndex * contentHeightPx, bodyCanvas.width, sliceHeightPx, 0, 0, bodyCanvas.width, sliceHeightPx);
                
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(headerCanvas.toDataURL('image/png'), 'PNG', pageMarginMm, pageMarginMm, contentWidthMm, headerHeightMm);
        pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', pageMarginMm, pageMarginMm + headerHeightMm + 3, contentWidthMm, sliceHeightPx / pxPerMm);

        pdf.setFontSize(9);
        pdf.setTextColor('#cbd5e1');
        pdf.text('CliniQueue Revenue Management System', pdfWidth / 2, pdfHeight - 10, { align: 'center' });
        pdf.text(`Page ${pageIndex + 1} of ${totalPages}`, pdfWidth - pageMarginMm, pdfHeight - 10, { align: 'right' });
      }
      pdf.save(fileName);
    } catch (err) {
      console.error(err);
    }
  };

  const activeData = filterType === 'month' ? monthlySummary?.summary : overview;
  const breakdownSource = doctorBreakdown || overview?.doctorBreakdown || monthlySummary?.summary?.doctorBreakdown;
  const totalPayments = (activeData?.cashRevenue || 0) + (activeData?.upiRevenue || 0) || 1;
  const cashPct = Math.round(((activeData?.cashRevenue || 0) / totalPayments) * 100);
  const upiPct = Math.round(((activeData?.upiRevenue || 0) / totalPayments) * 100);
  const collectionPct = activeData?.totalRevenue > 0 ? Math.round((activeData.collectedRevenue / activeData.totalRevenue) * 100) : 0;

  const ACCENT_COLORS = ['#1e3a8a','#047857','#d97706','#7c3aed','#dc2626','#0891b2'];
  const ACCENT_BG = ['#dbeafe','#dcfce7','#fef3c7','#ede9fe','#fee2e2','#cffafe'];

  const statCards = activeData ? (
    revenueType === 'subscription' ? [
      {
        label: 'Total Subscription Revenue', value: activeData.totalRevenue,
        sub: `${activeData.clinicCount || 0} Active Clinics`,
        borderCol: '#1e3a8a', htmlIcon: SVGAsset.dollar
      },
      {
        label: 'Collected Revenue', value: activeData.collectedRevenue,
        sub: 'Paid Invoices',
        borderCol: '#047857', pct: activeData.totalRevenue > 0 ? Math.round(activeData.collectedRevenue / activeData.totalRevenue * 100) : 0, htmlIcon: SVGAsset.check
      },
      {
        label: 'Pending Revenue', value: activeData.pendingRevenue,
        sub: 'Unpaid Invoices',
        borderCol: '#d97706',
        pct: activeData.totalRevenue > 0 ? Math.round(activeData.pendingRevenue / activeData.totalRevenue * 100) : 0,
        htmlIcon: SVGAsset.clock
      },
      {
        label: 'Active Subscriptions', value: activeData.activeSubscriptions || 0,
        sub: 'Current Subscriptions',
        borderCol: '#7c3aed', noINR: true, htmlIcon: SVGAsset.users
      },
    ] : [
      {
        label: 'Gross Revenue', value: activeData.totalRevenue,
        sub: `${activeData.patientCount || 0} Expected Invoices`,
        borderCol: '#1e3a8a', htmlIcon: SVGAsset.dollar
      },
      {
        label: 'Realized Amount', value: activeData.collectedRevenue,
        sub: 'Settled Cash + UPI Node',
        borderCol: '#047857', pct: collectionPct, htmlIcon: SVGAsset.check
      },
      {
        label: 'Pending Balance', value: activeData.pendingRevenue,
        sub: 'Awaiting Operator Remittance',
        borderCol: '#d97706',
        pct: activeData.totalRevenue > 0 ? Math.round(activeData.pendingRevenue / activeData.totalRevenue * 100) : 0,
        htmlIcon: SVGAsset.clock
      },
      {
        label: 'Patient Volume', value: activeData.patientCount || 0,
        sub: selectedDoctor ? `Dr. ${selectedDoctor.doctorName}` : 'All Providers Integrated',
        borderCol: '#7c3aed', noINR: true, htmlIcon: SVGAsset.users
      },
    ]
  ) : [];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@600;700&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }

        .cq-rev {
          overflow-x: hidden;
          max-width: 100%;
          min-width: 0;
          font-family: 'Plus Jakarta Sans', sans-serif;
          color: #111827;
        }
        .cq-rev * { box-sizing: border-box; min-width: 0; }

        @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
        @keyframes barRise { from{transform:scaleY(0);transform-origin:bottom} to{transform:scaleY(1)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes shimmer { 0%{background-position:200% center} 100%{background-position:-200% center} }

        .cq-card {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 16px rgba(15, 23, 42, 0.03);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cq-card:hover { border-color: #d1d5db; box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08); }

        .seg {
          display: flex; background: #f3f4f6;
          border-radius: 9px; padding: 3px; gap: 3px;
          border: 1px solid #e5e7eb;
        }
        .seg button {
          padding: 7px 14px; border: none; border-radius: 7px;
          font-size: 13px; font-weight: 600;
          cursor: pointer; color: #6b7280; background: transparent;
          display: flex; align-items: center; gap: 6px; transition: all 0.15s ease;
        }
        .seg button.on { background: #ffffff; color: #111827; box-shadow: 0 1px 2px rgba(0,0,0,0.05); border: 1px solid #f3f4f6; }

        .period { display: flex; background: #f3f4f6; border-radius: 9px; padding: 3px; gap: 3px; border: 1px solid #e5e7eb; }
        .period button { padding: 7px 12px; border: none; border-radius: 7px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; cursor: pointer; color: #6b7280; background: transparent; transition: all 0.12s ease; }
        .period button.on { background: #1e3a8a; color: #ffffff; box-shadow: 0 2px 6px rgba(30, 58, 138, 0.15); }

        .cq-input {
          background: #ffffff; border: 1px solid #d1d5db;
          border-radius: 9px; padding: 9px 12px;
          font-size: 13px; font-weight: 500;
          color: #111827; outline: none; transition: all 0.15s ease;
          height: 38px;
        }
        .cq-input:focus { border-color: #1e3a8a; box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1); background: #f0f4ff; }

        .pbar { height: 6px; background: #e5e7eb; border-radius: 99px; overflow: hidden; margin-top: 10px; }
        .pfill { height: 100%; border-radius: 99px; transition: width 0.6s ease; }

        .doc-row {
          padding: 14px 16px; border-radius: 12px; cursor: pointer;
          transition: all 0.2s ease; border: 1px solid #e5e7eb;
          display: flex; align-items: center; gap: 14px; background: #ffffff;
        }
        .doc-row:hover { background: #f9fafb; border-color: #d1d5db; transform: translateY(-1px); }

        .exp-btn {
          padding: 9px 18px; background: #0f172a;
          border: none; border-radius: 10px; color: #14f1c2; font-size: 13px; font-weight: 700;
          cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.15s ease;
          height: 38px;
        }
        .exp-btn:hover { background: #1e293b; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15); }

        .av {
          width: 34px; height: 34px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700; flex-shrink: 0;
        }

        .skeleton {
          background: linear-gradient(90deg,#f3f4f6 25%,#e5e7eb 50%,#f3f4f6 75%);
          background-size: 200% center; animation: shimmer 1.5s infinite; border-radius: 8px;
        }

        .dot-live { width: 6px; height: 6px; border-radius: 50%; background: #10b981; display: inline-block; box-shadow: 0 0 8px rgba(4, 120, 87, 0.5); }

        .rev-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .rev-table th {
          padding: 14px 16px; background: #f9fafb;
          font-size: 11px; font-weight: 700; color: #1e3a8a;
          text-transform: uppercase; letter-spacing: 0.5px;
          border-bottom: 1px solid #e5e7eb; text-align: left;
        }
        .rev-table th:nth-child(2), .rev-table th:nth-child(3), .rev-table th:nth-child(4), .rev-table th:nth-child(5) { text-align: right; }
        .rev-table th:last-child { text-align: right; }
        .rev-table td { padding: 14px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; vertical-align: middle; }
        .rev-table td:nth-child(2), .rev-table td:nth-child(3), .rev-table td:nth-child(4), .rev-table td:nth-child(5) { text-align: right; }
        .rev-table tr:last-child td { border-bottom: none; }
        .rev-table tbody tr { transition: background 0.12s ease; cursor: pointer; }
        .rev-table tbody tr:hover td { background: #f9fafb; }

        .stat-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 18px;
          margin-bottom: 28px;
        }

        .main-grid {
          display: grid;
          grid-template-columns: 1.4fr 0.6fr;
          gap: 20px;
          margin-bottom: 28px;
        }

        @media (max-width: 1150px) {
          .main-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 768px) {
          .fbar-wrap { flex-direction: column !important; align-items: stretch !important; gap: 12px !important; }
          .cq-rev { padding: 16px !important; }
          .fbar-wrap .cq-input { width: 100% !important; }
          .rev-table th, .rev-table td { white-space: nowrap; }
          .stat-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 480px) {
          .fbar-wrap { padding: 12px !important; }
          .fbar-wrap .seg, .fbar-wrap .period { width: 100%; }
          .fbar-wrap .cq-input, .fbar-wrap input[type="text"], .fbar-wrap select.cq-input { width: 100% !important; min-width: 0 !important; max-width: 100% !important; }
          .fbar-wrap > div:last-child { width: 100%; display:flex; gap:10px; align-items:center; }
          .fbar-wrap .exp-btn { width: 100%; justify-content: center; }
          .search-input { width: 100% !important; }
          .cq-rev { padding: 12px !important; }
          .main-grid { grid-template-columns: 1fr !important; }
          .rev-table th, .rev-table td { white-space: normal; }
        }
      `}</style>

      <div className="cq-rev" style={{
        padding: 32, minHeight: '100vh', background: '#fafbfc',
        opacity: mounted ? 1 : 0, transition: 'opacity 0.3s ease',
      }}>

        {/* ── Header Area ───────────────────────── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 24, marginBottom: 32, animation: 'fadeUp 0.4s ease both' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
              <span className="dot-live" />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#047857', letterSpacing: '1px', textTransform: 'uppercase' }}>Live Financial Stream</span>
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: '-0.4px', margin: 0, lineHeight: 1.2 }}>
              Central Ledger & Revenue Matrix
            </h1>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 6, margin: 0, fontWeight: 500 }}>
              Audit clinic operations, desk settlements, and provider output streams.
            </p>
          </div>
          <button className="exp-btn" onClick={exportPdf} style={{ flexShrink: 0, marginTop: 0, alignSelf: 'center' }}>
            <div dangerouslySetInnerHTML={{ __html: SVGAsset.download }} style={{ display: 'flex', alignItems: 'center', color: 'inherit' }} />
            Document Export
          </button>
        </div>

        {/* ── Control & Filtering Bar ────────────────── */}
        <div className="cq-card fbar-wrap" style={{ padding: '14px 18px', marginBottom: 28, display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', animation: 'fadeUp 0.4s 0.08s both' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="seg">
              <button className={revenueType === 'clinic' ? 'on' : ''} onClick={() => setRevenueType('clinic')}>
                <div dangerouslySetInnerHTML={{ __html: SVGAsset.users }} style={{ display: 'flex', alignItems: 'center', color: 'inherit' }} />
                Clinic Revenue
              </button>
              <button className={revenueType === 'subscription' ? 'on' : ''} onClick={() => setRevenueType('subscription')}>
                <div dangerouslySetInnerHTML={{ __html: SVGAsset.dollar }} style={{ display: 'flex', alignItems: 'center', color: 'inherit' }} />
                Subscription Revenue
              </button>
            </div>

            {revenueType === 'clinic' && (
              <div className="seg">
                <button className={viewMode === 'clinic' ? 'on' : ''} onClick={() => setViewMode('clinic')}>
                  <div dangerouslySetInnerHTML={{ __html: SVGAsset.users }} style={{ display: 'flex', alignItems: 'center', color: 'inherit' }} />
                  All Doctors
                </button>
                <button className={viewMode === 'doctor' ? 'on' : ''} onClick={() => setViewMode('doctor')}>
                  <div dangerouslySetInnerHTML={{ __html: SVGAsset.users }} style={{ display: 'flex', alignItems: 'center', color: 'inherit' }} />
                  Single Doctor
                </button>
              </div>
            )}

            {revenueType === 'clinic' && viewMode === 'clinic' && (
              <select className="cq-input" style={{ minWidth: 160, maxWidth: 200 }}
                value={selectedDoctor?.id || ''}
                onChange={e => handleDoctorChange(doctors.find(d => String(d.id) === e.target.value) || doctors[0])}>
                {doctors.map(d => <option key={d.id} value={d.id || ''}>{d.doctorName}</option>)}
              </select>
            )}

            <div className="period">
              {['today', 'month', 'year', 'customRange'].map(p => (
                <button key={p} className={filterType === p ? 'on' : ''} onClick={() => setFilterType(p)}>
                  {p === 'customRange' ? 'Custom Date' : p.toUpperCase()}
                </button>
              ))}
            </div>

            {filterType === 'month' && (
              <input type="month" className="cq-input" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} />
            )}
            {filterType === 'year' && (
              <input type="number" className="cq-input" style={{ width: 80 }} value={selectedYear} min="2020" max="2035"
                onChange={e => setSelectedYear(parseInt(e.target.value))} />
            )}
            {filterType === 'customRange' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="date" className="cq-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>to</span>
                <input type="date" className="cq-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', maxWidth: 420 }}>
              <div dangerouslySetInnerHTML={{ __html: SVGAsset.search }} style={{ position: 'absolute', left: 12, zIndex: 1, display: 'flex', alignItems: 'center', color: '#9ca3af' }} />
              <input type="text" autoComplete="off" className="cq-input search-input" style={{ paddingLeft: 36 }} placeholder="Search pending..."
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
              />
            </div>
            <button type="button" className="cq-input" style={{ cursor: 'pointer', background: '#fff', padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #d1d5db' }} onClick={() => loadData()} disabled={loading}>
              <div dangerouslySetInnerHTML={{ __html: SVGAsset.refresh }} style={{ display: 'flex', alignItems: 'center', color: 'inherit', animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            </button>
          </div>
        </div>

        {/* ── Metrics Cards Display ──────────────────────── */}
        <div className="stat-grid">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="cq-card" style={{ padding: 20 }}>
                <div className="skeleton" style={{ height: 12, width: '40%', marginBottom: 16 }} />
                <div className="skeleton" style={{ height: 26, width: '65%', marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 11, width: '50%' }} />
              </div>
            ))
          ) : activeData ? (
            statCards.map((card, i) => (
              <div key={i} className="cq-card" style={{ padding: 20, borderLeft: `3px solid ${card.borderCol}`, animation: `fadeUp 0.4s ${0.1+i*0.06}s ease both`, background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.5) 100%)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.4 }}>{card.label}</span>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: `${card.borderCol}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.borderCol, flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: card.htmlIcon }} />
                </div>
                <div style={{ fontSize: 26, fontWeight: 700, color: '#111827', letterSpacing: '-0.3px', marginBottom: 6, fontFamily: "'JetBrains Mono', monospace" }}>
                  <AnimCounter target={card.value} isINR={!card.noINR} />
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500, marginBottom: card.pct !== undefined ? 10 : 0 }}>{card.sub}</div>
                {card.pct !== undefined && (
                  <div className="pbar"><div className="pfill" style={{ width: `${card.pct}%`, background: card.borderCol }} /></div>
                )}
                {trendData && filterType === 'today' && !card.noINR && (
                  <div style={{ marginTop: 12, opacity: 0.8 }}>
                    <Sparkline data={trendData} color={card.borderCol} />
                  </div>
                )}
              </div>
            ))
          ) : null}
        </div>

        {/* ── Graphical Visualizations ───────────────────── */}
        {!loading && activeData && (
          <div className="main-grid">
            <div className="cq-card" style={{ padding: 22, animation: 'fadeUp 0.4s 0.24s both' }}>
              {filterType === 'today' && trendData ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div dangerouslySetInnerHTML={{ __html: SVGAsset.trend }} style={{ display: 'flex', alignItems: 'center', color: '#1e3a8a' }} />
                        7-Day Performance Continuum
                    </div>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: '#1e3a8a', fontWeight: 700, background: '#dbeafe', padding: '5px 12px', borderRadius: 7, border: '1px solid #bfdbfe' }}>
                      {formatINR(trendData.reduce((s, d) => s + (d.revenue || 0), 0))}
                    </div>
                  </div>
                  <BarChart data={trendData} />
                </>
              ) : filterType === 'month' && monthlySummary?.dailyBreakdown?.length > 0 ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
                    <div dangerouslySetInnerHTML={{ __html: SVGAsset.calendar }} style={{ display: 'flex', alignItems: 'center', color: '#1e3a8a' }} />
                    Daily Distribution Logs
                  </div>
                  <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                    <table className="rev-table">
                      <thead>
                        <tr>
                          {['Calendar Node','Gross Matrix','Cash Pool','UPI Protocol','Visits'].map(h => <th key={h}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {monthlySummary.dailyBreakdown.map((day, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 600, color: '#374151' }}>{new Date(day.date).toLocaleDateString('en-IN',{month:'short',day:'numeric'})}</td>
                            <td style={{ fontWeight: 700, color: '#111827', fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(day.revenue)}</td>
                            <td style={{ color: '#047857', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(day.cash)}</td>
                            <td style={{ color: '#0891b2', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(day.upi)}</td>
                            <td style={{ color: '#6b7280', fontWeight: 600 }}>{day.patientCount || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, color: '#9ca3af', gap: 8 }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>No tracking metrics logged for this window</span>
                </div>
              )}
            </div>

            <div className="cq-card" style={{ padding: 22, animation: 'fadeUp 0.4s 0.28s both' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e3a8a" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 2v10l8.5 5"/></svg>
                Allocation Framework
              </div>
              <PaymentDonutChart
                cashPct={cashPct}
                upiPct={upiPct}
                cashValue={activeData.cashRevenue}
                upiValue={activeData.upiRevenue}
              />
            </div>
          </div>
        )}

        {/* ── Provider Distribution Breakdowns ───────────── */}
        {!loading && revenueType === 'clinic' && viewMode === 'clinic' && !selectedDoctor && breakdownSource && breakdownSource.length > 0 && (
          <div className="cq-card" style={{ padding: 22, marginBottom: 28, animation: 'fadeUp 0.4s 0.32s both' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
              <div dangerouslySetInnerHTML={{ __html: SVGAsset.users }} style={{ display: 'flex', alignItems: 'center', color: '#1e3a8a' }} />
              Provider Yield Streams
            </div>
            <div style={{ borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <table className="rev-table">
                <thead>
                  <tr>
                    <th>Doctor Profile</th>
                    <th>Settled Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdownSource.map((doc, i) => {
                    const initials = (doc.doctorName || '??').split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase();
                    const col = ACCENT_COLORS[i % ACCENT_COLORS.length];
                    const bg  = ACCENT_BG[i % ACCENT_BG.length];
                    return (
                      <tr key={i} onClick={() => handleDoctorChange(doc)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="av" style={{ background: bg, color: col, fontWeight: 700 }}>{initials}</div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{doc.doctorName}</div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3, fontWeight: 500 }}>{doc.opdCount || doc.patientCount || 0} Case Encounters</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: '#1e3a8a' }}>
                          {formatINR(doc.revenue)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Subscription Clinic Breakdown ───────────────── */}
        {!loading && revenueType === 'subscription' && subscriptionClinicBreakdown && subscriptionClinicBreakdown.length > 0 && (
          <div className="cq-card" style={{ padding: 22, marginBottom: 28, animation: 'fadeUp 0.4s 0.32s both' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
              <div dangerouslySetInnerHTML={{ __html: SVGAsset.users }} style={{ display: 'flex', alignItems: 'center', color: '#1e3a8a' }} />
              Clinic Subscription Revenue
            </div>
            <div style={{ borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <table className="rev-table">
                <thead>
                  <tr>
                    <th>Clinic Name</th>
                    <th>Total Revenue</th>
                    <th>Collected</th>
                    <th>Pending</th>
                    <th>Invoices</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionClinicBreakdown.map((clinic, i) => {
                    const col = ACCENT_COLORS[i % ACCENT_COLORS.length];
                    const bg  = ACCENT_BG[i % ACCENT_BG.length];
                    const initials = (clinic.clinicName || '??').split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase();
                    return (
                      <tr key={i}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="av" style={{ background: bg, color: col, fontWeight: 700 }}>{initials}</div>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{clinic.clinicName}</div>
                              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3, fontWeight: 500 }}>ID: {clinic.clinicId}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: '#1e3a8a' }}>
                          {formatINR(clinic.totalRevenue)}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: '#047857' }}>
                          {formatINR(clinic.collectedRevenue)}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: '#d97706' }}>
                          {formatINR(clinic.pendingRevenue)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 14, color: '#7c3aed' }}>
                          {clinic.invoiceCount || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Subscription Plan Breakdown ───────────────── */}
        {!loading && revenueType === 'subscription' && subscriptionPlanBreakdown && subscriptionPlanBreakdown.length > 0 && (
          <div className="cq-card" style={{ padding: 22, marginBottom: 28, animation: 'fadeUp 0.4s 0.32s both' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
              <div dangerouslySetInnerHTML={{ __html: SVGAsset.dollar }} style={{ display: 'flex', alignItems: 'center', color: '#1e3a8a' }} />
              Plan-wise Subscription Revenue
            </div>
            <div style={{ borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
              <table className="rev-table">
                <thead>
                  <tr>
                    <th>Plan Name</th>
                    <th>Total Revenue</th>
                    <th>Invoice Count</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionPlanBreakdown.map((plan, i) => {
                    const col = ACCENT_COLORS[i % ACCENT_COLORS.length];
                    const bg  = ACCENT_BG[i % ACCENT_BG.length];
                    const initials = (plan.planName || '??').split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase();
                    return (
                      <tr key={i}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div className="av" style={{ background: bg, color: col, fontWeight: 700 }}>{initials}</div>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{plan.planName}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: '#1e3a8a' }}>
                          {formatINR(plan.totalRevenue)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 14, color: '#7c3aed' }}>
                          {plan.invoiceCount || 0}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Settlement Open Queue Pipeline ──────────────── */}
        {!loading && (
          <div className="cq-card" style={{ overflow: 'hidden', animation: 'fadeUp 0.4s 0.36s both', border: '1px solid #e5e7eb' }}>
            <div style={{ padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div dangerouslySetInnerHTML={{ __html: SVGAsset.calendar }} style={{ display: 'flex', alignItems: 'center', color: '#1e3a8a' }} />
                  {revenueType === 'subscription' ? 'Pending Invoices' : 'Settlement Queue Pipeline'}
                  {pendingPayments?.pending?.length > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#d97706', background: '#fef3c7', padding: '3px 9px', borderRadius: 8, border: '1px solid #fcd34d' }}>
                      {pendingPayments.pending.length} Open Accounts
                    </span>
                  )}
                </div>
              </div>
            </div>
            {revenueType === 'subscription' ? (
              // Subscription pending invoices table
              <div style={{ overflowX: 'auto' }}>
                {pendingPayments?.pending && pendingPayments.pending.length > 0 ? (
                  <table className="rev-table">
                    <thead>
                      <tr>
                        <th>Invoice Number</th>
                        <th>Clinic</th>
                        <th>Plan</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Generated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingPayments.pending.map((invoice, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 700, color: '#111827' }}>{invoice.invoiceNumber || `INV-${invoice.id}`}</td>
                          <td style={{ color: '#4b5563' }}>{invoice.clinicName || 'Unknown'}</td>
                          <td style={{ color: '#4b5563' }}>{invoice.planName || 'N/A'}</td>
                          <td style={{ fontWeight: 700, color: '#1e3a8a', fontFamily: "'JetBrains Mono', monospace" }}>{formatINR(invoice.totalAmount)}</td>
                          <td>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: invoice.status === 'PAID' ? '#dcfce7' : '#fef3c7', color: invoice.status === 'PAID' ? '#047857' : '#d97706' }}>
                              {invoice.status}
                            </span>
                          </td>
                          <td style={{ fontSize: 12, color: '#9ca3af' }}>
                            {invoice.generatedAt ? new Date(invoice.generatedAt).toLocaleDateString('en-IN') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>
                    <span>No pending invoices</span>
                  </div>
                )}
              </div>
            ) : (
              <PendingPaymentsWidget
                onOpenPayment={(appointment) => {
                  setSelectedPaymentAppointment(appointment);
                  if (patientReceiptEnabled && doctorReceiptSettings[appointment.doctorId] === true) {
                    setShowPaymentModal(true);
                  } else {
                    setShowRevenuePaymentModal(true);
                  }
                }}
              />
            )}
          </div>
        )}

        {/* ── Patient Invoice Creation Modal ───────────── */}
        {showPaymentModal && selectedPaymentAppointment && (
          <PatientInvoiceBillingModal
            clinicId={clinicSettings.id || auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__) || ''}
            onClose={() => { setShowPaymentModal(false); setSelectedPaymentAppointment(null); }}
            appointment={selectedPaymentAppointment}
            existingInvoice={selectedPaymentAppointment.patientInvoice || null}
            mode={selectedPaymentAppointment.patientInvoice && selectedPaymentAppointment.patientInvoice.invoiceStatus !== 'DRAFT' ? 'payment' : 'invoice'}
            onComplete={(invoice, action) => {
              if (action !== 'saved') {
                setShowPaymentModal(false);
                setSelectedPaymentAppointment(null);
              }
              loadData();
              window.dispatchEvent(new CustomEvent(action === 'paid' ? 'paymentCollected' : 'invoiceUpdated', {
                detail: { appointment: selectedPaymentAppointment, invoice, action }
              }));
            }}
          />
        )}

        {showRevenuePaymentModal && selectedPaymentAppointment && (
          <ConsultationPaymentModal
            isOpen={showRevenuePaymentModal}
            clinicId={clinicSettings.id || auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__) || ''}
            appointment={selectedPaymentAppointment}
            patientName={selectedPaymentAppointment.patientName}
            onClose={() => { setShowRevenuePaymentModal(false); setSelectedPaymentAppointment(null); }}
            onPaymentComplete={() => {
              setShowRevenuePaymentModal(false);
              setSelectedPaymentAppointment(null);
              loadData();
            }}
            onPaymentFailed={() => loadData()}
          />
        )}

      </div>
    </>
  );
};

export default RevenuePage;